import { ChevronRight } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  FormControlLabel,
  ListItem,
  Typography,
} from '@mui/material';
import React, { useMemo } from 'react';
import { ScopePagePair } from 'Types/dealGPT';

interface ScopeAccordionDrawerItemProps {
  scope: string;
  pages: string[];
  selectedScopePages: ScopePagePair[];
  toggleScope: (scope: string, pages: string[]) => void;
  toggleScopePage: (pair: ScopePagePair) => void;
  expanded: boolean;
  onToggleExpand: () => void;
}

const ScopeAccordionDrawerItem: React.FC<ScopeAccordionDrawerItemProps> = ({
  scope,
  pages,
  selectedScopePages,
  toggleScope,
  toggleScopePage,
  expanded,
  onToggleExpand,
}) => {
  const selectedPagesInScope = useMemo(
    () => selectedScopePages.filter((p) => p.scope === scope).map((p) => p.pageName),
    [selectedScopePages, scope]
  );

  const fullySelected = pages.every((p) => selectedPagesInScope.includes(p));
  const partiallySelected = selectedPagesInScope.length > 0 && !fullySelected;

  return (
    <Accordion
      disableGutters
      elevation={0}
      expanded={expanded}
      onChange={onToggleExpand}
      sx={{
        borderBottom: 'none',
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary
        expandIcon={null}
        sx={{
          pl: 0,
          py: 0.5,
          minHeight: 32,
          '& .MuiAccordionSummary-content': {
            margin: 0,
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
          <ChevronRight
            sx={{
              mr: 1,
              transition: 'transform 0.2s',
              transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
            }}
          />
          <Checkbox
            checked={fullySelected}
            indeterminate={partiallySelected}
            onChange={(e) => {
              e.stopPropagation();
              toggleScope(scope, pages);
            }}
            onClick={(e) => e.stopPropagation()}
          />
          <Typography>{scope}</Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ pl: 8, py: 0 }}>
        {pages.map((pageName) => (
          <ListItem key={`${scope}-${pageName}`} sx={{ pl: 0, py: 0.5 }}>
            <FormControlLabel
              label={pageName}
              control={
                <Checkbox
                  checked={selectedScopePages.some(
                    (p) => p.scope === scope && p.pageName === pageName
                  )}
                  onChange={() => toggleScopePage({ scope, pageName })}
                  onClick={(e) => e.stopPropagation()}
                />
              }
            />
          </ListItem>
        ))}
      </AccordionDetails>
    </Accordion>
  );
};

export default ScopeAccordionDrawerItem;
